import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import PasskeyService from '~/modules/services/passkey-service';

export const PASSKEYS_QUERY_KEY = ['passkeys'];

/**
 * The passkeys on this account, and adding or removing them.
 *
 * Simpler than the authenticator-app flow because there is nothing to show and nothing to
 * acknowledge: the secret never leaves the authenticator, so there is no QR code, no key to
 * transcribe and no recovery codes to save. The whole ceremony is one system prompt.
 */
export default function usePasskeys() {
	const queryClient = useQueryClient();
	const [error, setError] = useState(null);

	const { data, isLoading } = useQuery({
		queryKey: PASSKEYS_QUERY_KEY,
		queryFn: PasskeyService.list,
		// Nothing sensitive to keep fresh, but a stale list would offer to remove a passkey that
		// another tab has already removed.
		staleTime: 0,
		// A browser that cannot do WebAuthn has nothing to list, and asking would only produce
		// a request whose answer is unusable.
		enabled: PasskeyService.isSupported()
	});

	const refresh = () => queryClient.invalidateQueries({ queryKey: PASSKEYS_QUERY_KEY });

	const { mutateAsync: add, isLoading: isAdding } = useMutation({
		mutationFn: PasskeyService.register,
		onMutate: () => setError(null),
		onSuccess: () => refresh(),
		onError: (e) => {
			// A dismissed system prompt arrives here as a DOMException. It is a decision rather
			// than a failure, so it is not worth a banner -- but a refused password or an
			// unconfigured deployment is.
			if(e?.name === 'NotAllowedError' || e?.name === 'AbortError') {
				return;
			}

			setError(e?.message || 'Could not add that passkey');
		}
	});

	const { mutateAsync: remove, isLoading: isRemoving } = useMutation({
		mutationFn: PasskeyService.remove,
		onMutate: () => setError(null),
		onSuccess: () => refresh(),
		onError: (e) => setError(e?.message || 'Could not remove that passkey')
	});

	const { mutateAsync: rename, isLoading: isRenaming } = useMutation({
		mutationFn: PasskeyService.rename,
		onMutate: () => setError(null),
		onSuccess: () => refresh(),
		onError: (e) => setError(e?.message || 'Could not rename that passkey')
	});

	return {
		supported: PasskeyService.isSupported(),
		configured: !!data?.configured,
		passwordRequired: !!data?.passwordRequired,
		// What adding another one asks for: `passkey` once this account holds one, `password`
		// while it holds none and has one, `none` when it has neither. The API decides.
		confirmWith: data?.confirmWith || 'none',
		// Whether there is a way back in without the device in front of them: a synced passkey
		// is its own backup, a device-bound one is not.
		backedUp: !!data?.backedUp,
		passkeys: data?.passkeys || [],
		isLoading,
		error,
		setError,
		add,
		isAdding,
		remove,
		isRemoving,
		rename,
		isRenaming,
		isBusy: isAdding || isRemoving || isRenaming
	};
}
